import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams, useLocation } from 'react-router-dom';
import StoreBadges from './StoreBadges';

/* CSS wave bars logo */
const WaveBars = () => (
  <div style={{ display: 'flex', gap: '2.5px', alignItems: 'center', height: '22px' }}>
    {[10, 16, 22, 16, 10].map((h, i) => (
      <span
        key={i}
        className="footer-bar"
        style={{
          display: 'block',
          width: '3px',
          height: `${h}px`,
          borderRadius: '2px',
          background: 'var(--v2-primary)',
          transformOrigin: 'center',
          animation: 'bar 1.2s ease-in-out infinite',
          animationDelay: `${i * 0.15}s`,
        }}
      />
    ))}
  </div>
);

const Footer = () => {
  const { t, i18n } = useTranslation();
  const { locale } = useParams<{ locale?: string }>();
  const location = useLocation();

  const getLocaleFromPath = () => {
    const segments = location.pathname.split('/').filter(Boolean);
    const supported = ['en', 'es', 'fr', 'de', 'pt', 'vi', 'sv', 'ro'];
    if (segments.length > 0 && supported.includes(segments[0])) return segments[0];
    return i18n.language || 'en';
  };
  const currentLocale = locale || getLocaleFromPath();

  const productLinks = [
    { label: t('nav.features', 'Features'), href: `/${currentLocale}/features` },
    { label: t('nav.pricing', 'Pricing'), href: `/${currentLocale}/pricing` },
    { label: t('footer.product.download', 'Download'), href: `/${currentLocale}/download` },
  ];

  const compareLinks = [
    { label: t('footer.compare.granola', 'Notely vs Granola'), href: `/${currentLocale}/notely-vs-granola` },
    { label: t('footer.compare.wisprFlow', 'Notely vs Wispr Flow'), href: `/${currentLocale}/notely-vs-wispr-flow` },
    { label: t('footer.compare.superwhisper', 'Notely vs SuperWhisper'), href: `/${currentLocale}/notely-vs-superwhisper` },
    { label: t('footer.compare.macwhisper', 'Notely vs MacWhisper'), href: `/${currentLocale}/notely-vs-macwhisper` },
  ];

  const companyLinks = [
    { label: t('footer.company.contact', 'Contact'), href: `/${currentLocale}/contact` },
    { label: t('footer.company.about', 'About'), href: `/${currentLocale}/about` },
  ];

  const legalLinks = [
    { label: t('footer.termsOfUse', 'Terms of Use'), href: '/terms' },
    { label: t('footer.privacyPolicy', 'Privacy Policy'), href: '/privacy' },
  ];

  const linkStyle: React.CSSProperties = {
    color: 'var(--v2-ink-muted)',
    textDecoration: 'none',
    fontSize: '14px',
    transition: 'color 0.15s',
  };

  const headingStyle: React.CSSProperties = {
    fontSize: '14px',
    fontWeight: 600,
    color: 'var(--v2-ink)',
    marginBottom: '16px',
  };

  return (
    <footer
      className="footer-outer"
      style={{
        position: 'relative',
        zIndex: 2,
        background: 'var(--v2-bg-subtle)',
        borderTop: '1px solid var(--v2-line)',
        color: 'var(--v2-ink)',
        padding: '64px 24px 40px',
        paddingBottom: 'calc(40px + env(safe-area-inset-bottom, 0px))',
      }}
    >
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        {/* 5-column grid */}
        <div
          className="footer-grid"
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '40px',
            marginBottom: '48px',
          }}
        >
          {/* Col 1: Brand */}
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px' }}>
              <WaveBars />
              <span style={{ fontWeight: 700, fontSize: '16px', color: 'var(--v2-ink)' }}>
                Notely AI
              </span>
            </div>
            <p style={{ fontSize: '13px', color: 'var(--v2-ink-muted)', lineHeight: 1.65, maxWidth: '220px' }}>
              {t('footer.tagline', 'Turn any audio into perfect notes with the power of AI.')}
            </p>
            <StoreBadges style={{ marginTop: '20px' }} />
          </div>

          {/* Col 2: Product */}
          <div>
            <div style={headingStyle}>{t('footer.product.heading', 'Product')}</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {productLinks.map((link) =>
                link.href.startsWith('#') ? (
                  <a key={link.label} href={link.href} onClick={(e) => e.preventDefault()} style={linkStyle}>
                    {link.label}
                  </a>
                ) : (
                  <Link key={link.label} to={link.href} style={linkStyle}>
                    {link.label}
                  </Link>
                )
              )}
            </div>
          </div>

          {/* Col 3: Company */}
          <div>
            <div style={headingStyle}>{t('footer.company.heading', 'Company')}</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {companyLinks.map((link) =>
                link.href.startsWith('#') ? (
                  <a key={link.label} href={link.href} onClick={(e) => e.preventDefault()} style={linkStyle}>
                    {link.label}
                  </a>
                ) : (
                  <Link key={link.label} to={link.href} style={linkStyle}>
                    {link.label}
                  </Link>
                )
              )}
            </div>
          </div>

          {/* Col 4: Compare */}
          <div>
            <div style={headingStyle}>{t('footer.compare.heading', 'Compare')}</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {compareLinks.map((link) => (
                <Link key={link.label} to={link.href} style={linkStyle}>
                  {link.label}
                </Link>
              ))}
            </div>
          </div>

          {/* Col 5: Legal */}
          <div>
            <div style={headingStyle}>{t('footer.legal.heading', 'Legal')}</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {legalLinks.map((link) => (
                <a key={link.label} href={link.href} onClick={link.href.startsWith('#') ? (e) => e.preventDefault() : undefined} style={linkStyle}>
                  {link.label}
                </a>
              ))}
            </div>
          </div>
        </div>

        {/* Bottom row */}
        <div
          style={{
            borderTop: '1px solid var(--v2-line)',
            paddingTop: '24px',
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '8px',
          }}
        >
          <span style={{ fontSize: '13px', color: 'var(--v2-ink-muted)' }}>
            {t('footer.copyright', { year: new Date().getFullYear() })}
          </span>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
